import { useMutation, useQueryClient } from '@tanstack/react-query';
import LeadService, {
  IUpdateLeadParams,
} from '@clickersphere/core/services/LeadService';
import { useBuildQueryKey } from '../useBuildQueryKey';
import { LeadQueryData, LEADS_QUERY_KEY } from './useLeads';

export interface IUpdateLeadVariables {
  id: string;
  data: IUpdateLeadParams;
  optimisticFields?: Partial<Omit<LeadQueryData, 'id'>>;
}

export function useUpdateLead() {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();

  const { isPending: updateLeadIsPending, mutateAsync: updateLeadAsync } =
    useMutation({
      mutationFn: async ({ id, data }: IUpdateLeadVariables) =>
        LeadService.update(id, { ...data }),
      onMutate: async (variables) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(LEADS_QUERY_KEY),
        });
        await queryClient.cancelQueries({
          queryKey: buildQueryKey([LEADS_QUERY_KEY, variables.id]),
        });
        const oldLeads = queryClient.getQueryData<LeadQueryData[]>(
          buildQueryKey(LEADS_QUERY_KEY),
        );
        const oldLead = queryClient.getQueryData<LeadQueryData>(
          buildQueryKey([LEADS_QUERY_KEY, variables.id]),
        );

        queryClient.setQueryData<LeadQueryData[]>(
          buildQueryKey(LEADS_QUERY_KEY),
          (old) =>
            old?.map((lead) =>
              lead.id === variables.id
                ? {
                    ...lead,
                    ...variables.data,
                    ...variables.optimisticFields,
                    queryStatus: 'pending',
                  }
                : lead,
            ),
        );

        queryClient.setQueryData<LeadQueryData>(
          buildQueryKey([LEADS_QUERY_KEY, variables.id]),
          (old) =>
            old?.id === variables.id
              ? {
                  ...old,
                  ...variables.data,
                  ...variables.optimisticFields,
                  queryStatus: 'pending',
                }
              : old,
        );

        return { oldLeads, oldLead };
      },
      onSuccess: async (_data, variables) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(LEADS_QUERY_KEY),
        });

        queryClient.setQueryData<LeadQueryData[]>(
          buildQueryKey(LEADS_QUERY_KEY),
          (old) =>
            old?.map((lead) =>
              lead.id === variables.id
                ? { ...lead, queryStatus: undefined }
                : lead,
            ),
        );

        queryClient.setQueryData<LeadQueryData>(
          buildQueryKey([LEADS_QUERY_KEY, variables.id]),
          (old) =>
            old?.id === variables.id ? { ...old, queryStatus: undefined } : old,
        );
      },

      onError: async (_error, variables, context) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(LEADS_QUERY_KEY),
        });
        await queryClient.cancelQueries({
          queryKey: buildQueryKey([LEADS_QUERY_KEY, variables.id]),
        });
        queryClient.setQueryData(
          buildQueryKey(LEADS_QUERY_KEY),
          context?.oldLeads,
        );
        queryClient.setQueryData(
          buildQueryKey([LEADS_QUERY_KEY, variables.id]),
          context?.oldLead,
        );
      },
    });

  return {
    updateLeadAsync,
    updateLeadIsPending,
  };
}
